import { createContext, useEffect } from 'react';
import { Map as olMap, View } from 'ol';
import { Group as LayerGroup } from 'ol/layer';
import { Attribution, defaults as defaultControls } from 'ol/control';
import * as olProj from 'ol/proj';
import { defaults as defaultInteractions } from 'ol/interaction';
import { useState } from 'react';
import {
	getPoiFeatures,
	householdLayers,
} from '../component/content/layer';
import axios from 'axios';
import { cql2filter } from '../common/func/cql2filter';

const mapView = new View({
	center: olProj.transform([128.0292, 36.6332], 'EPSG:4326', 'EPSG:3857'),
	projection: 'EPSG:3857',
	zoom: 7.5,
	enableRotation: false,
});

const MapContext = createContext({
	mode: 'basic',
	pastMapRef: null,
	currMapRef: null,
});

const MapProvider = ({ children }) => {
	const [mode, setMode] = useState('intro'); // intro, agglomerate, location

	const [map, setMap] = useState(null);

	const [collapseBgMap, setCollapseBgMap] = useState(true);
	const [bgMap, setBgMap] = useState('basic');
	
	const [emdBoundary, setEmdBoundary] = useState(false);

	const [aggGrid, setAggGrid] = useState(true);
	const [collapsePopGrid, setCollapsePopGrid] = useState(false);
	const [gridOpacity, setGridOpacity] = useState(1);

	const [sidoList, setSidoList] = useState([]);
	const [sidoCd, setSidoCd] = useState('');
	const [sggList, setSggList] = useState([]);
	const [sggCd, setSggCd] = useState('');

	const [minInput, setMinInput] = useState(100);
	const [maxInput, setMaxInput] = useState(19330.0);

	const [poiInfo, setPoiInfo] = useState([]);

	const [selectPoi, setSelectPoi] = useState('');
	const [selectDistance, setSelectDistance] = useState('');
	const [selectStandard, setSelectStandard] = useState('');

	const [agglomerate, setAgglomerate] = useState(true);
	const [location, setLocation] = useState(true);
	const [industry, setIndustry] = useState(true);

	const [popupOpen, setPopupOpen] = useState(false);
	const [popupData, setPopupData] = useState({
		sido_nm: '',
		sgg_nm: '',
		id: '',
		toH: 0,
		oneH: 0,
		allPop: 0,
		pop_49: 0,
		pop_65: 0,
		pop_75: 0,
	});
	const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
	const [popupBind, setPopupBind] = useState({ x: 0, y: 0 });

	const createMap = (id, bgLayer = [], otherLayer = [], poiLayer = []) => {
		return new olMap({
			target: id,
			view: mapView,
			layers: [
				new LayerGroup({
					name: 'bgLayer',
					layers: bgLayer,
				}),
				new LayerGroup({
					name: 'otherLayer',
					layers: otherLayer,
				}),
				new LayerGroup({
					name: 'poiLayer',
					layers: poiLayer,
				}),
			],
			controls: defaultControls({ zoom: true, attribution: false }).extend([
				new Attribution({ collapsible: false }),
			]),
			interactions: defaultInteractions().extend([]),
		});
	};

	const getPoi = async (sgg) => {
		for (let i = 1; i < 6; i++) {
			const cqlFilter =
				sgg !== '' ? `sgg_cd like '${sgg}%' and type=${i}` : sgg === '' ? `type=${i}` : null;
			const xmlString = await cql2filter(cqlFilter);
			getPoiFeatures(xmlString, i);
		};
	};

	const getPopupData = (e, data) => {
		setPopupOpen(true);
		setPopupPosition({
			x: e.originalEvent.clientX,
			y: e.originalEvent.clientY,
		});
		setPopupData({
			sido_nm: data.sido_nm,
			sgg_nm: data.sgg_nm,
			id: `${data.grid_1k_cd}`,
			toH: data.to_hh_21,
			oneH: data.one_hh_21,
			allPop: data.all_pop21,
			pop_49: data.pop_1549_2,
			pop_65: data.old_pop21,
			pop_75: data.f75_pop_16,
		});
	};

	const featureClickCallBack = async ({ e, target_map: targetMap }) => {
		const viewResolution = targetMap.getView().getResolution();
		const url = householdLayers.sphGridLayer
			.getSource()
			.getFeatureInfoUrl(e.coordinate, viewResolution, 'EPSG:3857', {
				INFO_FORMAT: 'application/json',
			});
		if (url) {
			console.log(url)
			axios({
				method: 'GET',
				url,
				withCredentials: true,
			})
			.then((res) => {
					if (res.status === 200) {
						const { data } = res;
						if (data.features.length > 0) {
							const data_ = data.features[0].properties;
							getPopupData(e, data_);
						} else {
							setPopupOpen(false);
						}
					}
				})
				.catch((error) => {
					console.log(error);
				});
		}
	};

	const setZoom = () => {
		if (map) {
			map.getView().setZoom(6.5);
			map
				.getView()
				.setCenter(
					olProj.transform([128.0292, 36.6332], 'EPSG:4326', 'EPSG:3857')
				);
		}
	};

	return (
		<MapContext.Provider
			value={{
				createMap,
				mode,
				map,
				collapseBgMap,
				bgMap,
				emdBoundary,
				aggGrid,
				collapsePopGrid,
				gridOpacity,
				sidoList,
				sidoCd,
				sggList,
				sggCd,
				minInput,
				maxInput,
				poiInfo,
				selectPoi,
				selectDistance,
				selectStandard,
				agglomerate,
				location,
				industry,
				popupOpen,
				popupData,
				popupPosition,
				popupBind,

				featureClickCallBack,
				setZoom,
				getPoi,

				setMode,
				setMap,
				setCollapseBgMap,
				setBgMap,
				setEmdBoundary,
				setAggGrid,
				setCollapsePopGrid,
				setGridOpacity,
				setSidoList,
				setSidoCd,
				setSggList,
				setSggCd,
				setMinInput,
				setMaxInput,
				setPoiInfo,
				setSelectPoi,
				setSelectDistance,
				setSelectStandard,
				setAgglomerate,
				setLocation,
				setIndustry,
				setPopupOpen,
				setPopupData,
				setPopupPosition,
				setPopupBind,
			}}
		>
			{children}
		</MapContext.Provider>
	);
};

export { MapContext, MapProvider };
